'use strict';
/* ==========================================================================
   lessons/roots: a VERBATIM COPY of lessons/narrator/core-local/zine.js (2026-10-02; lesson-local, never shared).
   Do not edit here except to fix a bug that is fixed there too. In this lesson there is no core-local/narration.js:
   Zine.mark / Zine.label fall back to the neon yellow, and core-local/wordkit.js draws this lesson's own pieces
   (strips, tiles, the press). Narrator's header follows.
   --------------------------------------------------------------------------
   core-local/zine.js — the lesson's ZINE look (DESIGN.md §2), drawn through the collage engine: photocopied pages with
   toner speckle taped and stapled to a corkboard, typewriter text, neon highlighter, doodles, a small stage with a
   spotlight for the open mic. COPIED from lessons/simile/core-local/zine.js (lesson-local: never shared, never edited
   there) and extended for the CASE BOARD (DESIGN.md §2): index-card case files, push pins, red string, evidence tags in
   the clue colours (core-local/narration.js Narration.CLUES), POV "camera" eyes.

   Performance rules (shared/engine/v2/README.md): the page paper is a cached paper piece (fixed size + seed); the toner
   speckle and the cork grain are canvases baked ONCE per size/seed (kept in a small cache, drawn as images); no
   shadowOn(); highlighter swipes and doodles are a few path fills / ink strokes. Everything is a pure function of T and
   the t0 you pass (scrubbable).

   Zine.C                          colours {ink, paper, toner, cork, red, …}; Zine.FONT = the typewriter face
                                   ('Special Elite', the repo's typewriter font in shared/assets/fonts: no new font files)
   Zine.cork()                     the corkboard backdrop (full stage)
   Zine.page(x, y, w, h, o)        a photocopied page, centre x, y {seed, rot, s, staple: 'left'|'top'|false, tape: n,
                                   depth, color, dark (toner density 0..1)}; returns nothing (draws)
   Zine.speckle(w, h, seed, dark)  the cached toner canvas of a page (drawn by page(); exposed for other shapes)
   Zine.type(str, x, y, size, o)   typewriter text that types on {t0, cps (chars/s, default 30), color, align, alpha,
                                   cursor} → the full width; t0 = -1e6 for static text
   Zine.typed(str, t0, cps)        how many characters are showing
   Zine.measure(str, size)         width in the typewriter face
   Zine.wrap(str, maxW, size)      lines
   Zine.highlight(x, y, w, h, colour, o)   a neon marker swipe, x, y = left-centre {t0, dur (0.35 s), seed, rot, alpha,
                                   prog (overrides t0)}: multiply blend, rough ends, drawn on left → right
   Zine.mark(id, x, y, w, h, o)    = highlight in the id's colour (Narration.colour: a clue, a POV, 'reliable' /
                                   'unreliable')
   Zine.doodle(id, x, y, r, o)     the device's doodle icon in ink {t0, dur, prog, color, seed, w}: simile = TWO WAVY
                                   LINES (the "≈" is always drawn, never a glyph: the fonts lack it), metaphor "=",
                                   personification a face, hyperbole "!!", alliteration "SSS" curls, onomatopoeia a burst,
                                   idiom a jigsaw piece, allusion an open book, irony a U-turn arrow, rhetorical question
                                   a "?", none a flat dash
   Zine.simileMark(x, y, w, o)     the two wavy lines alone
   Zine.label(id, x, y, o)         a label chip: neon paper strip + doodle + the name typed {size, rot, s, seed, text}
                                   (id = a clue id → the EVIDENCE TAG "SAYS vs DOES" … in its clue colour)
   Zine.staple(x, y, o)            a staple {rot, len}
   Zine.stage(x, y, w, o)          a small stage (front at y) with a spotlight cone from above {t0, light (0..1), seed}
   Zine.art(name, x, y, h, o)      a linocut cut-out (IMG[name]) with its ink stand-in until the art is in
                                   {ax, ay, rot, s, seed}: microphone_stand, spotlight, typewriter (copied from simile);
                                   volcano, dog, vinegar_bottle, magnifier, case_file (new: DESIGN.md §2, the assets agent)
   Zine.stamp(text, x, y, o)       a rubber stamp (UI.stamp in the zine red): FACT-CHECKED / RELIABLE / UNRELIABLE
   ---- the case board (new in this lesson) ----
   Zine.card(x, y, w, h, o)        an index card (a case file), centre x, y {seed, rot, s, title (typed above the red
                                   header rule), lines (blue ruled lines, default true), pin (a push pin at the top), color, t0}
   Zine.pin(x, y, o)               a push pin {color (default the zine red), r (default 14)}
   Zine.string(x1, y1, x2, y2, o)  red string pinned at both ends, sagging {prog (0..1, drawn from end 1), t0 + dur (instead
                                   of prog), sag (px), color, w, pins (true)}: pure in T
   Zine.eye(x, y, r, o)            a linocut eye: a POV "camera", one per head the narrator enters {open (0..1), color, seed,
                                   fill (the iris colour), label (typed under it)}
   Zine.clueDoodle(id)             the doodle a clue / POV id draws (e.g. 'says-vs-does' → the U-turn arrow, a POV → an eye)
   ========================================================================== */
(function () {
  const C = {
    ink: '#141210', paper: '#f7f5ef', toner: '#1c1a17', cork: '#b98a58', corkDark: '#8f6437', red: '#d9352c',
    grey: '#6d6a64', kraft: '#c99a67', white: '#fffefa', tape: 'rgba(236,232,214,0.82)',
  };
  const FONT = "'Special Elite'";
  const dev = id => (window.Narration ? Narration.colour(id) : '#fff04a');
  // a clue / POV / verdict id draws one of the zine's doodles (a clue has no glyph of its own)
  const CLUE_DOODLE = { 'says-vs-does': 'irony', 'others-react': 'personification', contradicts: 'hyperbole', 'something-to-gain': 'coin',
    'missing-info': 'rhetoricalQuestion', 'cant-be-true': 'onomatopoeia', 'big-feelings': 'heart', 'first-limited': 'eye', second: 'eye',
    'third-limited': 'eye', 'third-omniscient': 'eye', reliable: 'metaphor', unreliable: 'hyperbole' };
  const clueDoodle = id => CLUE_DOODLE[id] || id;

  /* ---------------- small canvas cache (speckle, cork grain) ---------------- */
  const cache = new Map(), MAX = 24;
  function baked(key, w, h, draw) {
    let c = cache.get(key);
    if (c) { cache.delete(key); cache.set(key, c); return c; }
    c = document.createElement('canvas'); c.width = Math.max(1, Math.round(w)); c.height = Math.max(1, Math.round(h));
    draw(c.getContext('2d'), c.width, c.height);
    cache.set(key, c);
    while (cache.size > MAX) cache.delete(cache.keys().next().value);
    return c;
  }

  /* toner speckle at half resolution: dust dots, a few clumps, faint copier streaks, a darker border (the glass edge) */
  function speckle(w, h, seed = 1, dark = 0.5) {
    const W = Math.round(w / 2), H = Math.round(h / 2);
    return baked(`sp${W}x${H}:${seed}:${dark}`, W, H, (x, cw, ch) => {
      const r = rng(seed * 97 + 13);
      const n = Math.round(cw * ch / 260 * (0.6 + dark));
      for (let i = 0; i < n; i++) {
        const px = r() * cw, py = r() * ch, s = r() < 0.92 ? 0.35 + r() * 0.6 : 0.9 + r() * 1.6;
        x.fillStyle = `rgba(20,18,16,${0.18 + r() * 0.55 * dark})`;
        x.beginPath(); x.arc(px, py, s, 0, TAU); x.fill();
      }
      // clumps
      for (let i = 0; i < 6 + dark * 8; i++) {
        const cx = r() * cw, cy = r() * ch;
        for (let k = 0; k < 18; k++) { x.fillStyle = `rgba(20,18,16,${0.2 + r() * 0.4})`; x.fillRect(cx + (r() - 0.5) * 10, cy + (r() - 0.5) * 6, 0.8, 0.8); }
      }
      // copier streaks (vertical, faint)
      for (let i = 0; i < 2 + Math.round(dark * 3); i++) {
        const sx = r() * cw, a = 0.03 + r() * 0.05 * dark;
        const gr = x.createLinearGradient(0, 0, 0, ch); gr.addColorStop(0, `rgba(20,18,16,0)`); gr.addColorStop(0.3 + r() * 0.4, `rgba(20,18,16,${a})`); gr.addColorStop(1, 'rgba(20,18,16,0)');
        x.fillStyle = gr; x.fillRect(sx, 0, 0.6 + r() * 1.6, ch);
      }
      // the glass edge: a grey band just inside the border
      const e = 3 + dark * 3;
      x.fillStyle = `rgba(20,18,16,${0.05 + dark * 0.07})`;
      x.fillRect(0, 0, cw, e); x.fillRect(0, ch - e, cw, e); x.fillRect(0, 0, e, ch); x.fillRect(cw - e, 0, e, ch);
    });
  }

  function cork() {
    g.fillStyle = paperTile(C.cork, 3, 'tex_kraft'); g.fillRect(0, 0, Game.W, Game.H);
    const c = baked('cork', 960, 540, (x, cw, ch) => {
      const r = rng(4242);
      for (let i = 0; i < 9000; i++) {
        const light = r() < 0.4;
        x.fillStyle = light ? `rgba(236,200,150,${0.15 + r() * 0.25})` : `rgba(70,42,18,${0.12 + r() * 0.3})`;
        const s = 0.5 + r() * 1.4; x.fillRect(r() * cw, r() * ch, s, s * (0.6 + r()));
      }
      const v = x.createRadialGradient(cw / 2, ch / 2, ch * 0.3, cw / 2, ch / 2, cw * 0.62);
      v.addColorStop(0, 'rgba(0,0,0,0)'); v.addColorStop(1, 'rgba(40,22,8,0.28)');
      x.fillStyle = v; x.fillRect(0, 0, cw, ch);
    });
    g.drawImage(c, 0, 0, Game.W, Game.H);
  }

  function staple(x, y, { rot = 0, len = 44 } = {}) {
    g.save(); g.translate(x, y); g.rotate(rot);
    g.lineCap = 'round';
    g.strokeStyle = 'rgba(40,40,44,0.5)'; g.lineWidth = 6; g.beginPath(); g.moveTo(-len / 2, 2); g.lineTo(len / 2, 2); g.stroke();
    g.strokeStyle = '#9ea2a8'; g.lineWidth = 4; g.beginPath(); g.moveTo(-len / 2, 0); g.lineTo(len / 2, 0); g.stroke();
    g.strokeStyle = 'rgba(255,255,255,0.7)'; g.lineWidth = 1.2; g.beginPath(); g.moveTo(-len / 2 + 3, -1); g.lineTo(len / 2 - 3, -1); g.stroke();
    g.restore();
  }

  function page(x, y, w, h, { seed = 1, rot = 0, s = 1, staple: st = false, tape: nTape = 0, depth = 1.3, color = C.paper, dark = 0.5 } = {}) {
    if (s <= 0.001) return;
    const W = Math.round(w), H = Math.round(h);
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    paperRect(0, 0, W, H, color, { seed, depth, boil: false });
    const sp = speckle(W, H, seed, dark);
    g.save(); g.globalCompositeOperation = 'multiply'; g.drawImage(sp, -W / 2, -H / 2, W, H); g.restore();
    if (st === 'left') { staple(-W / 2 + 34, -H * 0.3, { rot: Math.PI / 2 + 0.05 }); staple(-W / 2 + 34, H * 0.3, { rot: Math.PI / 2 - 0.04 }); }
    else if (st === 'top') staple(-W / 2 + 60, -H / 2 + 34, { rot: -0.7 });
    if (nTape >= 1) tape(0, -H / 2 + 4, Math.min(190, W * 0.3), 0.03 + (seed % 3) * 0.02, C.tape, seed + 3);
    if (nTape >= 2) { tape(-W / 2 + 40, H / 2 - 20, 150, -0.75, C.tape, seed + 5); tape(W / 2 - 40, H / 2 - 20, 150, 0.75, C.tape, seed + 6); }
    g.restore();
  }

  /* ---------------- typewriter text ---------------- */
  const widths = new Map();
  function measure(str, size) {
    const k = size + '|' + str;
    let w = widths.get(k);
    if (w == null) { g.save(); g.font = `${size}px ${FONT}`; w = g.measureText(str).width; g.restore(); widths.set(k, w); if (widths.size > 4000) widths.clear(); }
    return w;
  }
  const typed = (str, t0, cps = 30) => (T < t0 ? 0 : Math.min(str.length, Math.floor((T - t0) * cps) + 1));
  function type(str, x, y, size, { t0 = -1e6, cps = 30, color = C.ink, align = 'left', alpha = 1, cursor = false, seed = 1 } = {}) {
    str = String(str);
    const full = measure(str, size), n = typed(str, t0, cps);
    if (n <= 0) return full;
    const x0 = align === 'center' ? x - full / 2 : align === 'right' ? x - full : x;
    g.save(); g.font = `${size}px ${FONT}`; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillStyle = color;
    g.globalAlpha *= alpha;
    const base = g.globalAlpha;                    // the caller's alpha (a fade) times ours
    // uneven typewriter strikes: every character a little up/down and lighter/darker (deterministic per index)
    const shown = str.slice(0, n);
    if (shown.length <= 90) {
      for (let i = 0; i < shown.length; i++) {
        const ch = shown[i]; if (ch === ' ') continue;
        const cx = x0 + measure(shown.slice(0, i), size), j = hash1(seed * 131 + i * 7);
        g.globalAlpha = base * (0.78 + 0.22 * j);
        g.fillText(ch, cx, y + (hash1(seed * 17 + i) - 0.5) * size * 0.06);
      }
    } else g.fillText(shown, x0, y);
    if (cursor && n < str.length && Math.floor(T * 3) % 2 === 0) { g.globalAlpha = base; g.fillRect(x0 + measure(shown, size) + 2, y - size * 0.4, size * 0.08, size * 0.8); }
    g.restore();
    return full;
  }
  function wrap(str, maxW, size) {
    const words = String(str).split(/\s+/), out = []; let line = '';
    for (const w of words) { const t = line ? line + ' ' + w : w; if (measure(t, size) > maxW && line) { out.push(line); line = w; } else line = t; }
    if (line) out.push(line);
    return out;
  }

  /* ---------------- neon highlighter ---------------- */
  function highlight(x, y, w, h, colour, { t0 = -1e6, dur = 0.35, seed = 1, rot = 0, alpha = 0.72, prog = null } = {}) {
    const p = prog != null ? clamp(prog) : E.outQuad(P(T, t0, t0 + dur));
    if (p <= 0) return;
    const r = rng(seed * 53 + 7), L = w * p, hh = h / 2;
    const top = [], bot = [];
    const n = Math.max(3, Math.round(L / 40));
    for (let i = 0; i <= n; i++) { const u = i / n; top.push([u * L, -hh + (r() - 0.5) * h * 0.12]); bot.push([u * L, hh + (r() - 0.5) * h * 0.12]); }
    g.save(); g.translate(x, y); g.rotate(rot - 0.006);
    g.globalCompositeOperation = 'multiply'; g.globalAlpha *= alpha; g.fillStyle = colour;
    g.beginPath();
    g.moveTo(-h * 0.08, -hh * 0.8);
    top.forEach(([px, py]) => g.lineTo(px, py));
    g.lineTo(L + h * 0.1, 0);                      // the chisel tip
    bot.slice().reverse().forEach(([px, py]) => g.lineTo(px, py));
    g.lineTo(-h * 0.1, hh * 0.85);
    g.closePath(); g.fill();
    // a darker second pass where the marker overlapped
    g.globalAlpha *= 0.35; g.fillRect(L * 0.1, -hh * 0.9, L * 0.25, hh * 0.5);
    g.restore();
  }
  const mark = (id, x, y, w, h, o = {}) => highlight(x, y, w, h, dev(id), o);

  /* ---------------- doodles (ink; the simile mark is two wavy lines, never a glyph) ---------------- */
  const wave = (x0, x1, y, amp, n = 2) => { const p = []; for (let i = 0; i <= 24; i++) { const u = i / 24; p.push([x0 + (x1 - x0) * u, y + Math.sin(u * TAU * n) * amp]); } return p; };
  function simileMark(x, y, w, { color = C.ink, seed = 1, prog = 1, lw = null } = {}) {
    const a = w * 0.1, gap = w * 0.22, lwid = lw || Math.max(2.5, w * 0.08);
    ink(wave(x - w / 2, x + w / 2, y - gap / 2, a), { w: lwid, color, seed, prog, passes: 1, step: 8, amp: 0.5 });
    ink(wave(x - w / 2, x + w / 2, y + gap / 2, a), { w: lwid, color, seed: seed + 1, prog, passes: 1, step: 8, amp: 0.5 });
  }
  function doodle(id, x, y, r, { t0 = -1e6, dur = 0.5, prog = null, color = C.ink, seed = 1, w = null } = {}) {
    const p = prog != null ? clamp(prog) : P(T, t0, t0 + dur);
    if (p <= 0) return;
    const lw = w || Math.max(2.5, r * 0.14), o = { w: lw, color, seed, prog: p, passes: 1, step: 8, amp: 0.6 };
    const circ = (cx, cy, rr, a0 = 0, a1 = TAU, n = 28) => { const q = []; for (let i = 0; i <= n; i++) { const a = a0 + (a1 - a0) * i / n; q.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr]); } return q; };
    const dot = (cx, cy, rr) => { if (p < 0.95) return; g.save(); g.fillStyle = color; g.beginPath(); g.arc(cx, cy, rr, 0, TAU); g.fill(); g.restore(); };
    switch (clueDoodle(id)) {
      case 'eye': {
        const q = [];
        for (let i = 0; i <= 16; i++) { const a = Math.PI * i / 16; q.push([x - Math.cos(a) * r * 0.95, y - Math.sin(a) * r * 0.55]); }
        for (let i = 0; i <= 16; i++) { const a = Math.PI * i / 16; q.push([x + Math.cos(a) * r * 0.95, y + Math.sin(a) * r * 0.55]); }
        ink(q, { ...o }); dot(x, y, r * 0.26); break;
      }
      case 'coin':
        ink(circ(x, y, r * 0.8), { ...o });
        ink([[x, y - r * 0.5], [x, y + r * 0.5]], { ...o, seed: seed + 1 });
        ink(circ(x, y - r * 0.12, r * 0.28, Math.PI * 0.2, Math.PI * 1.3, 8), { ...o, seed: seed + 2 });
        ink(circ(x, y + r * 0.14, r * 0.28, Math.PI * 1.2, Math.PI * 2.3, 8), { ...o, seed: seed + 3 }); break;
      case 'heart': {
        const q = [[x, y + r * 0.75]];
        circ(x - r * 0.38, y - r * 0.2, r * 0.4, Math.PI * 0.75, Math.PI * 2, 10).forEach(pt => q.push(pt));
        circ(x + r * 0.38, y - r * 0.2, r * 0.4, Math.PI, Math.PI * 2.25, 10).forEach(pt => q.push(pt));
        q.push([x, y + r * 0.75]);
        ink(q, { ...o }); break;
      }
      case 'simile': simileMark(x, y, r * 1.6, { color, seed, prog: p, lw }); break;
      case 'metaphor':
        ink([[x - r * 0.8, y - r * 0.25], [x + r * 0.8, y - r * 0.25]], { ...o });
        ink([[x - r * 0.8, y + r * 0.25], [x + r * 0.8, y + r * 0.25]], { ...o, seed: seed + 1 }); break;
      case 'personification':
        ink(circ(x, y, r * 0.8), { ...o });
        dot(x - r * 0.28, y - r * 0.18, lw * 0.7); dot(x + r * 0.28, y - r * 0.18, lw * 0.7);
        ink(circ(x, y + r * 0.02, r * 0.45, 0.35, Math.PI - 0.35, 12), { ...o, seed: seed + 2 }); break;
      case 'hyperbole':
        for (const dx of [-0.32, 0.32]) { ink([[x + dx * r, y - r * 0.85], [x + dx * r * 0.8, y + r * 0.3]], { ...o, seed: seed + dx * 10 }); dot(x + dx * r * 0.78, y + r * 0.72, lw * 0.9); }
        ink([[x - r * 0.95, y - r * 0.95], [x - r * 0.62, y - r * 0.6]], { ...o, w: lw * 0.6, seed: seed + 7 });
        ink([[x + r * 0.95, y - r * 0.95], [x + r * 0.62, y - r * 0.6]], { ...o, w: lw * 0.6, seed: seed + 8 }); break;
      case 'alliteration':
        for (let k = 0; k < 3; k++) {
          const cx = x + (k - 1) * r * 0.62, q = [];
          for (let i = 0; i <= 16; i++) { const u = i / 16; q.push([cx + Math.sin(u * TAU) * r * 0.22 * (1 - u * 0.1), y - r * 0.6 + u * r * 1.2]); }
          ink(q, { ...o, seed: seed + k * 3 });
        } break;
      case 'onomatopoeia': {
        const q = starPts(r * 0.95, 8, 0.55).map(([a, b]) => [x + a, y + b]);
        ink(q, { ...o }); break;
      }
      case 'idiom': {
        const s = r * 0.7, q = [[x - s, y - s], [x - s * 0.2, y - s]];
        circ(x, y - s, s * 0.22, Math.PI, TAU, 8).forEach(pt => q.push(pt));
        q.push([x + s, y - s], [x + s, y - s * 0.2]);
        circ(x + s, y, s * 0.22, -Math.PI / 2, Math.PI / 2, 8).forEach(pt => q.push(pt));
        q.push([x + s, y + s], [x - s, y + s], [x - s, y - s]);
        ink(q, { ...o }); break;
      }
      case 'allusion':
        ink([[x, y - r * 0.55], [x, y + r * 0.7]], { ...o });
        ink([[x, y - r * 0.55], [x - r * 0.45, y - r * 0.72], [x - r * 0.95, y - r * 0.6], [x - r * 0.95, y + r * 0.6], [x - r * 0.45, y + r * 0.5], [x, y + r * 0.7]], { ...o, seed: seed + 1 });
        ink([[x, y - r * 0.55], [x + r * 0.45, y - r * 0.72], [x + r * 0.95, y - r * 0.6], [x + r * 0.95, y + r * 0.6], [x + r * 0.45, y + r * 0.5], [x, y + r * 0.7]], { ...o, seed: seed + 2 }); break;
      case 'irony': {
        const q = [[x - r * 0.45, y + r * 0.8], [x - r * 0.45, y - r * 0.15]];
        circ(x, y - r * 0.15, r * 0.45, Math.PI, TAU, 14).forEach(pt => q.push(pt));
        q.push([x + r * 0.45, y + r * 0.45]);
        ink(q, { ...o });
        if (p > 0.9) ink([[x + r * 0.15, y + r * 0.2], [x + r * 0.45, y + r * 0.55], [x + r * 0.75, y + r * 0.2]], { ...o, seed: seed + 4, prog: 1 }); break;
      }
      case 'rhetoricalQuestion': {
        const q = circ(x, y - r * 0.35, r * 0.45, Math.PI * 1.05, Math.PI * 2.45, 16);
        q.push([x, y + r * 0.35]);
        ink(q, { ...o }); dot(x, y + r * 0.78, lw * 0.9); break;
      }
      default: ink([[x - r * 0.8, y], [x + r * 0.8, y]], { ...o });
    }
  }

  /* ---------------- a device label chip ---------------- */
  function label(id, x, y, { size = 34, rot = 0, s = 1, seed = 1, text = null } = {}) {
    if (s <= 0.001) return;
    const t = text || (window.Narration ? Narration.name(id) : id).toUpperCase();
    const tw = measure(t, size), w = Math.round(tw + size * 2.6), h = Math.round(size * 1.5);
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    paperRect(0, 0, w, h, dev(id), { seed, depth: 0.7, boil: false });
    doodle(id, -w / 2 + size * 0.95, 0, size * 0.42, { seed: seed + 3, w: Math.max(2, size * 0.08) });
    type(t, -w / 2 + size * 1.75, size * 0.04, size, { seed });
    g.restore();
    return w;
  }

  /* ---------------- the stage ---------------- */
  function stage(x, y, w, { t0 = -1e6, light = 1, seed = 1 } = {}) {
    const on = light * P(T, t0, t0 + 0.4);
    // the spotlight cone from above, then the pool of light on the boards
    if (on > 0) {
      g.save(); g.globalCompositeOperation = 'lighter';
      const top = y - w * 0.95, gr = g.createLinearGradient(0, top, 0, y);
      gr.addColorStop(0, `rgba(255,246,210,${0.32 * on})`); gr.addColorStop(1, `rgba(255,246,210,${0.06 * on})`);
      g.fillStyle = gr;
      g.beginPath(); g.moveTo(x - w * 0.06, top); g.lineTo(x + w * 0.06, top); g.lineTo(x + w * 0.3, y - 10); g.lineTo(x - w * 0.3, y - 10); g.closePath(); g.fill();
      g.fillStyle = `rgba(255,246,210,${0.22 * on})`;
      g.beginPath(); g.ellipse(x, y - 8, w * 0.32, w * 0.05, 0, 0, TAU); g.fill();
      g.restore();
    }
    // the boards: a black-painted platform with a lighter lip
    paperRect(x, y + 34, Math.round(w), 68, '#262321', { seed: seed + 1, depth: 1.2, boil: false });
    paperRect(x, y + 4, Math.round(w + 16), 14, '#4a4440', { seed: seed + 2, depth: 0.5, boil: false });
    for (let i = 1; i < 6; i++) ink([[x - w / 2 + i * w / 6, y + 12], [x - w / 2 + i * w / 6, y + 66]], { w: 2, color: 'rgba(255,255,255,0.12)', seed: seed * 10 + i, passes: 1, step: 30 });
  }

  /* ---------------- linocut cut-outs, with ink stand-ins until the art is in ---------------- */
  function art(name, x, y, h, { ax = 0.5, ay = 1, rot = 0, s = 1, seed = 5 } = {}) {
    if (s <= 0.001) return;
    if (IMG[name]) { cut(name, x, y, h, { ax, ay, rot, s, seed, depth: 1.1 }); return; }
    // stand-in: a white card with a thick black ink sketch (linocut-ish), feet/base at (x, y) for ay = 1
    const cy = y - h * (ay - 0.5);
    g.save(); g.translate(x, cy); g.rotate(rot); g.scale(s, s);
    const W = Math.round(h * 0.7), H = Math.round(h);
    paperRect(0, 0, W, H, C.white, { seed, depth: 0.9, boil: false });
    const o = { w: Math.max(4, h * 0.035), color: C.ink, seed, passes: 1, step: 10 };
    const r = h * 0.3;
    switch (name) {
      case 'microphone_stand':
        ink([[0, -H * 0.3], [0, H * 0.42]], o); ink([[-r * 0.6, H * 0.42], [r * 0.6, H * 0.42]], o);
        inkCircle(0, -H * 0.36, r * 0.2, o); break;
      case 'spotlight':
        ink([[-r * 0.5, -r * 0.3], [r * 0.5, -r * 0.3], [r * 0.8, r * 0.4], [-r * 0.8, r * 0.4], [-r * 0.5, -r * 0.3]], o);
        ink([[0, -r * 0.3], [0, -H * 0.4]], o); break;
      case 'typewriter': case 'volcano': case 'dog': case 'vinegar_bottle': case 'magnifier': case 'case_file':
      default:
        inkCircle(0, 0, r * 0.8, o);
        g.save(); g.font = `${Math.round(h * 0.09)}px ${FONT}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = C.grey;
        g.fillText(name.replace('_', ' '), 0, H * 0.4); g.restore();
    }
    g.restore();
  }

  function stamp(text, x, y, o = {}) { UI.stamp(text, x, y, { color: C.red, ...o }); }

  /* ---------------- the case board ---------------- */
  function pin(x, y, { color = C.red, r = 14 } = {}) {
    g.save();
    g.fillStyle = 'rgba(40,22,8,0.35)'; g.beginPath(); g.ellipse(x + r * 0.35, y + r * 0.45, r * 0.95, r * 0.7, 0, 0, TAU); g.fill();
    g.fillStyle = color; g.beginPath(); g.arc(x, y, r, 0, TAU); g.fill();
    g.fillStyle = 'rgba(255,255,255,0.55)'; g.beginPath(); g.arc(x - r * 0.35, y - r * 0.35, r * 0.32, 0, TAU); g.fill();
    g.strokeStyle = C.ink; g.lineWidth = 2; g.beginPath(); g.arc(x, y, r, 0, TAU); g.stroke();
    g.restore();
  }
  /* red string: a sagging curve from (x1, y1) to (x2, y2), drawn from end 1 as prog goes 0 → 1 */
  function string(x1, y1, x2, y2, { prog = null, t0 = -1e6, dur = 0.45, sag = null, color = '#c8231c', w = 4, pins = true } = {}) {
    const p = prog != null ? clamp(prog) : E.outQuad(P(T, t0, t0 + dur));
    if (p <= 0) return;
    const d = Math.hypot(x2 - x1, y2 - y1), sg = sag == null ? Math.min(90, d * 0.12) : sag;
    const mx = (x1 + x2) / 2, my = (y1 + y2) / 2 + sg, n = 28;
    const at = u => [(1 - u) * (1 - u) * x1 + 2 * u * (1 - u) * mx + u * u * x2, (1 - u) * (1 - u) * y1 + 2 * u * (1 - u) * my + u * u * y2];
    g.save(); g.lineCap = 'round'; g.lineJoin = 'round';
    for (const [dx, dy, col, lw] of [[2, 3, 'rgba(40,22,8,0.3)', w + 1], [0, 0, color, w]]) {
      g.strokeStyle = col; g.lineWidth = lw; g.beginPath();
      for (let i = 0; i <= n; i++) { const [px, py] = at((i / n) * p); if (i) g.lineTo(px + dx, py + dy); else g.moveTo(px + dx, py + dy); }
      g.stroke();
    }
    g.restore();
    if (pins) { pin(x1, y1, { r: 9 }); if (p >= 0.999) pin(x2, y2, { r: 9 }); }
  }
  /* an index card: white, a red header rule, blue ruled lines */
  function card(x, y, w, h, { seed = 1, rot = 0, s = 1, title = null, lines = true, pin: pinned = true, color = C.white, t0 = -1e6 } = {}) {
    if (s <= 0.001) return;
    const W = Math.round(w), H = Math.round(h);
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    paperRect(0, 0, W, H, color, { seed, depth: 1.1, boil: false });
    const top = -H / 2 + Math.min(70, H * 0.22);
    g.strokeStyle = 'rgba(217,53,44,0.75)'; g.lineWidth = 2.5; g.beginPath(); g.moveTo(-W / 2 + 10, top); g.lineTo(W / 2 - 10, top); g.stroke();
    if (lines) { g.strokeStyle = 'rgba(82,140,200,0.35)'; g.lineWidth = 1.5; for (let yy = top + 46; yy < H / 2 - 12; yy += 46) { g.beginPath(); g.moveTo(-W / 2 + 10, yy); g.lineTo(W / 2 - 10, yy); g.stroke(); } }
    if (title) type(String(title), -W / 2 + 26, (top - H / 2) / 2 + 4, Math.round(Math.min(40, (top + H / 2) * 0.5)), { t0, cps: 40, seed: seed + 11 });
    if (pinned) pin(0, -H / 2 + 20);
    g.restore();
  }
  /* a POV camera: a linocut eye (almond outline, iris, pupil, lashes) that opens with `open` */
  function eye(x, y, r, { open = 1, color = C.ink, seed = 1, fill = '#52c8ff', label: lab = null } = {}) {
    const o = clamp(open), lid = u => (1 - Math.pow(2 * u - 1, 2)) * r * 0.5 * o;
    const almond = () => { g.beginPath(); g.moveTo(-r, 0); g.quadraticCurveTo(0, -r * o, r, 0); g.quadraticCurveTo(0, r * o, -r, 0); };
    g.save(); g.translate(x, y);
    g.fillStyle = C.white; almond(); g.fill();
    if (o > 0.15) {
      g.save(); almond(); g.clip();
      g.fillStyle = fill; g.beginPath(); g.arc(0, 0, r * 0.42, 0, TAU); g.fill();
      g.fillStyle = C.ink; g.beginPath(); g.arc(0, 0, r * 0.2, 0, TAU); g.fill();
      g.fillStyle = C.white; g.beginPath(); g.arc(-r * 0.1, -r * 0.1, r * 0.07, 0, TAU); g.fill();
      g.restore();
    }
    const up = [], dn = [];
    for (let i = 0; i <= 14; i++) { const u = i / 14, xx = -r + 2 * r * u; up.push([xx, -lid(u)]); dn.push([xx, lid(u)]); }
    const lw = Math.max(3, r * 0.12);
    ink(up, { w: lw, color, seed, passes: 1, step: 8, amp: 0.5 });
    ink(dn, { w: lw, color, seed: seed + 1, passes: 1, step: 8, amp: 0.5 });
    for (let k = -2; k <= 2; k++) { const u = 0.5 + k * 0.15, ly = -lid(u); ink([[-r + 2 * r * u, ly], [-r + 2 * r * u + k * r * 0.08, ly - r * 0.22]], { w: Math.max(2, r * 0.07), color, seed: seed + 5 + k, passes: 1, step: 8 }); }
    g.restore();
    if (lab) type(String(lab), x, y + r * 0.95, Math.round(r * 0.55), { align: 'center', seed: seed + 9 });
  }

  window.Zine = { C, FONT, cork, page, speckle, type, typed, measure, wrap, highlight, mark, doodle, simileMark, label, staple, stage, art, stamp,
    pin, string, card, eye, clueDoodle };
})();
